import PropTypes from 'prop-types';
import React, { useState, useEffect, useCallback } from 'react';

import Formsy from 'formsy-react';
import { ChevronDown, ChevronUp, GripVertical } from 'lucide-react';
import { toJS } from 'mobx';
import { FormBody, Row, Col, Button, ButtonIcon, Label, FormGroup, SimpleAlert, DefaultTooltip, TabsList, Tabs, TabsTrigger, TabsContent, FormFooter, FormCard } from 'twyne-new.ui'

import ConfirmationPopover from '@/components/ConfirmationPopover';
import { buttonLabels } from '@Components/rules/options';
import FieldFormLayout from '@Modules/path/views/fields/containers/Form'
import Input from 'components/form/Input';
import Select from 'components/form/Select';
import Switch from 'components/form/Switch';

import { DndSortableList, removeScaleY, useSortableDndListItem } from '@/components/SortableDnd';

import { usePageStore } from '../store';

import ConsentText, { hasConsentText } from './ConsentText';
import CustomField, { hasCustomField } from './CustomField';
import LivePreview from './LivePreview';
import PlacementModified from './PlacementModified';
import QuestionAnswers, { hasQuestionAnswers } from './QuestionAnswers';
import AdvancedTab from './Tabs/Advanced';
import PagePathsTab from './Tabs/PagePathsTab';
import PageRulesTab from './Tabs/PageRulesTab';
import PingPostTab from './Tabs/PingPost';
import PixelTab from './Tabs/Pixel';
import PreviewTab from './Tabs/Preview';
import StandardTab from './Tabs/Standard';
import { typeOptions, generatePlacement, templateAlias } from './formUtils';
import { TcpaContextProvider } from './forms/tcpa-question-custom/context';
import { TemplateBuilderContent } from './forms/tcpa-question-custom/tabs';
import history from '@/utils/history';

class PageForm extends React.Component {
  state = {
    type: null,
    validatePlacementTimeout: 5000,
    active: true,
    activeTab: 'advanced',
    extraParams: '',
    extraFields: [],
    metaFields: [],
    integrationData: {
      sanitary: true,
    },
    cfid: null,
    customFieldOpen: false,
    isVersionView: false,
    viewClosed: false,
    conflictEvent: null,
    compilerError: false,
    integration: null,
    detailsOpen: true,
    sectionOrder: null,
  };

  handleCompilerError = error => {
    this.setState({ compilerError: error })
  }

  handleChangeTcpaCustomContent = value => {
    this.setState({ content: value })
  }

  handleConflict = event => {
    this.setState({ conflictEvent: event })
  }

  componentDidMount() {
    const { data } = this.props;
    if (data) {
      this.setState({
        rulesProps: JSON.stringify(data.rules),
        integrationDataProps: JSON.stringify(data.integrationData),
      })
      if (!data.integrationData[templateAlias[data.type]]) {
        data.integrationData[templateAlias[data.type]] = {};
        data.integrationData.sanitary = false;
      }
      if (['partner-twyne', 'partner-ifficient'].includes(data.type) && data.pixels) {
        data.pixels = data.pixels.map(({ id }) => id)
      }
      this.setState(toJS(data));
      if (data.type && data.type !== 'iframe' && data.type !== 'html') {
        this.setState({ activeTab: 'standard' });
      }

      this.setState({ isVersionView: !!this.props.onCloseView });
    }
  }

  componentDidUpdate(prevProps) {
    if (this.state.isVersionView !== !!this.props.onCloseView) {
      this.setState({ isVersionView: !!this.props.onCloseView })
    }

    if (prevProps.data !== this.props.data) {
      this.setState({ ...this.props.data, viewClosed: this.props.viewClosed })
    }
  }

  handleChangeType = async option => {
    const type = option ? option.value : null;
    this.setState({ type });

    if (type && type !== 'iframe' && type !== 'html') {
      const { integrationData } = this.state;
      if (!integrationData[templateAlias[type]]) {
        integrationData[templateAlias[type]] = {};
      }
      if (this.state.integrationData.sanitary && type !== 'tcpa-question-custom') {
        this.setState({ content: await generatePlacement(type, integrationData) });
      }
      this.setState({ integrationData, activeTab: 'standard' });
    } else {
      this.setState({ activeTab: 'advanced' });
    }
  }

  handleChangeIntegration = async (field, value) => {
    if (this.state.viewClosed) {
      this.setState({ viewClosed: false })
      return
    }
    const { type } = this.state;
    const { integrationData } = this.state;
    integrationData[templateAlias[type]][field] = value;
    this.setState({ integrationData });
    if (type !== 'tcpa-question-custom') {
      if (field !== 'alwaysShow' && !(type === 'tcpa-form' && field === 'manualSubmit')) {
        this.setState({ content: await generatePlacement(type, integrationData) });
      }
    }
  }

  handleChangePingPost = async (field, value) => {
    const { type } = this.state;
    const { integrationData } = this.state;
    integrationData[templateAlias[type]][field] = value;
    this.setState({ integrationData });
  }

  handleClickRestorePlacement = async () => {
    const { type } = this.state;
    const { integrationData } = this.state;
    integrationData.sanitary = true;
    this.setState({ integrationData });
    integrationData.vendorPlacement = undefined;
    if (type !== 'tcpa-question-custom') {
      this.setState({ content: await generatePlacement(type, integrationData) })
    }
  }

  handleImportPlacement = async value => {
    const { type } = this.state;
    const { integrationData } = this.state;
    integrationData.sanitary = true;
    this.setState({ integrationData });
    integrationData.vendorPlacement = value;
    this.setState({ content: await generatePlacement(type, integrationData) });
  }

  handleChangeContent = value => {
    const { integrationData } = this.state;
    integrationData.sanitary = false;
    this.setState({ integrationData });
    this.setState({ content: value })
  }

  handleChange = state => {
    this.setState(state);
  }

  checkVersionChanges = () => {
    if (this.props.restored) {
      return true
    }
    if (!this.props.data) {
      return false
    }
    if (this.props.data.content !== this.state.content) {
      return true
    }
    if (this.state.rulesProps !== JSON.stringify(this.state.rules)) {
      return true
    }
    if (this.state.integrationDataProps !== JSON.stringify(this.state.integrationData)) {
      return true
    }
    return false
  }

  handleValidSubmit = model => {
    const { type, versionChanges } = this.state;
    const integrationData = {
      [templateAlias[type]]: this.state.integrationData[templateAlias[type]],
      dynamicNavigation: this.state.integrationData.dynamicNavigation,
      sanitary: this.state.integrationData.sanitary,
    }
    model.integrationData = integrationData;
    this.props.handleSubmit(Object.assign(this.state, model), versionChanges && model.versionName || null);
  }

  DynamicNavigation = () => {
    const { type } = this.state
    if (type && (['html', 'iframe', 'partner-twyne', 'partner-ifficient', 'account-pw'].includes(type) || type.startsWith('tcpa-'))) {
      const handleChange = value => this.setState(({ integrationData }) => {
        integrationData.dynamicNavigation = value
        return { integrationData, dynamicNavigation: value }
      }, async () => {
        const { integrationData } = this.state
        if (type !== "tcpa-question-custom" && type !== 'iframe' && type !== 'html') this.setState({ content: await generatePlacement(type, integrationData) });
      })

      const disabled = !this.state.integrationData.sanitary && !['html', 'iframe'].includes(type)

      return <Col size='2'>
        <FormGroup>
          <Label>Dynamic Navigation</Label>
          <Switch
            disabled={disabled || this.state.isVersionView}
            name="dynamicNavigation"
            value={this.state.integrationData.dynamicNavigation || false}
            onChange={handleChange} />
        </FormGroup>
      </Col>
    }

    return null
  }

  CustomFields = () => {
    const { integrationData, customFieldOpen, cfid } = this.state
    const { lists: { customFields: { items: customFields, useLoad }, 'meta-fields': { items: metaFields, useLoad: useLoadMetaFields } } } = usePageStore()

    const loadFields = useLoad()
    useLoadMetaFields()

    const parseFields = useCallback(async (customFields, metaFields, integrationData) => {
      let extraParams = '';
      const extraFields = [];
      for (const customField of customFields) {
        if (!customField.private) {
          extraFields.push({ ...customField, key: `cf_${customField.key}` });
          extraParams += `&cf_${customField.key}={cf_${customField.key}}`;
        }
      }
      for (const metaField of metaFields) {
        extraParams += `&${metaField.key}={${metaField.key}}`;
      }
      integrationData.extraParams = extraParams;
      integrationData.extraFields = extraFields;
      integrationData.metaFields = metaFields;
      this.setState({ extraParams, extraFields, metaFields, integrationData, customFieldOpen: false });
    }, [])

    useEffect(() => {
      if (integrationData && (customFields?.length || metaFields?.length)) {
        parseFields(customFields || [], metaFields || [], integrationData)
      }
    }, [customFields, metaFields, parseFields, integrationData])

    if (!customFieldOpen) {
      return null;
    }

    return <FieldFormLayout key={cfid || ''} isCustomFieldPage={false} cfid={cfid}
      onSave={loadFields} onCancel={() => this.setState({ customFieldOpen: false })} />
  }

  MultiBuyers = () => {
    const { type } = this.state

    if (!MULTI_BUYER_TYPES.includes(type)) {
      return null
    }

    return <Col>
      <FormCard title='Multi Buyers'>
        <PingPostTab
          data={this.state.integrationData[templateAlias[this.state.type]]}
          onChangeData={this.handleChangeIntegration}
          integration={this.state.integration}
          onChangeIntegration={this.handleChange}
          disabled={this.state.isVersionView}
        />
      </FormCard>
    </Col>
  }

  get hasLivePreview() {
    return hasQuestionAnswers(this.state.type)
  }

  get hasCustomField() {
    return hasCustomField(this.state.type)
  }

  get isTcpaQuestion() {
    return this.state.type === 'tcpa-question'
  }

  CustomField = () => <CustomField
    type={this.state.type}
    integrationData={this.state.integrationData}
    disabled={!this.state.integrationData.sanitary || this.state.isVersionView}
    onChange={this.handleChangeIntegration}
    onCustomFieldClick={this.handleChange}
  />

  PageSettings = () => <>
    <Col size="2">
      <FormGroup>
        <Label>Validation Timeout (ms)</Label>
        <Input
          type="number"
          name="validatePlacementTimeout"
          value={this.state.validatePlacementTimeout}
          required
          disabled={this.state.isVersionView}
        />
      </FormGroup>
    </Col>
    <Col size="2">
      <FormGroup>
        <Label>Hide Hosted Submit</Label>
        <Switch
          name="hideHostedSubmit"
          value={this.state.hideHostedSubmit || false}
          onChange={hideHostedSubmit => this.setState({ hideHostedSubmit })}
          disabled={this.state.isVersionView}
        />
      </FormGroup>
    </Col>
    <this.DynamicNavigation />
  </>

  Sections = () => {
    const { type } = this.state
    const sections = {
      general: <this.General />,
      details: <this.Details />,
      questionAnswers: hasQuestionAnswers(type) && <this.QuestionAnswers />,
      consentText: hasConsentText(type) && <this.ConsentText />,
      multiBuyers: MULTI_BUYER_TYPES.includes(type) && <this.MultiBuyers />,
      rules: <this.Rules />,
    }

    const order = (this.state.sectionOrder || (this.isTcpaQuestion ? TCPA_QUESTION_SECTION_ORDER : SECTION_ORDER))
      .filter(id => sections[id])

    return <Row>
      <DndSortableList
        items={order.map(id => ({ id }))}
        onSortEnd={sorted => this.setState({ sectionOrder: sorted.map(({ id }) => id) })}
        activationConstraint={{ distance: 8 }}
      >
        {order.map(id => <SortableSection key={id} id={id}>
          {sections[id]}
        </SortableSection>)}
      </DndSortableList>
    </Row>
  }

  General = () =>
    <Col>
      <FormCard title={<span className='flex items-center gap-2'>
      General
      <this.PlacementModified />
    </span>}>
        <Row>
          <Col size="4">
            <FormGroup>
              <Label>Name</Label>
              <Input
                name="name"
                value={this.state.name || ''}
                required
                disabled={this.state.isVersionView}
              />
            </FormGroup>
          </Col>
          <Col size={this.hasCustomField ? '4' : '2'}>
            <FormGroup>
              <Label>Type</Label>
              <Select
                name="type"
                value={this.state.type}
                options={typeOptions}
                handleOnChange={this.handleChangeType}
                required
                disabled={!this.state.integrationData.sanitary || this.state.isVersionView}
              />
            </FormGroup>
          </Col>
          <this.CustomField />
          {!this.hasCustomField && <this.PageSettings />}
        </Row>
      </FormCard>
    </Col>

  Details = () => <Col>
    <FormCard
      title={<span className='flex items-center gap-2'>
        Behavior &amp; advanced settings
        <this.PlacementModified />
      </span>}
      classNameBody={this.state.detailsOpen ? undefined : 'hidden'}
      headerButtons={<div className='flex items-center gap-4'>
        <this.PublishedToggle />
        <ButtonIcon
          content={this.state.detailsOpen ? 'Collapse' : 'Expand'}
          onClick={() => this.setState(({ detailsOpen }) => ({ detailsOpen: !detailsOpen }))}
        >
          {this.state.detailsOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </ButtonIcon>
      </div>}
    >
      {this.renderTabs()}
    </FormCard>
  </Col>

  PlacementModified = () => <PlacementModified
    show={!this.state.integrationData.sanitary}
    onRestore={this.handleClickRestorePlacement}
  />

  PublishedToggle = () => {
    if (!this.state.id) {
      return null
    }

    return <Button size='sm' variant='outline' color='accent' onClick={() => this.props.onChangeView('preview')}>
      Live preview
    </Button>
  }

  LivePreview = () => <LivePreview
    type={this.state.type}
    integrationData={this.state.integrationData}
  >
    <this.PublishedToggle />
  </LivePreview>

  QuestionAnswers = () => <QuestionAnswers
    badge={<this.PlacementModified />}
    type={this.state.type}
    integrationData={this.state.integrationData}
    disabled={!this.state.integrationData.sanitary || this.state.isVersionView}
    onChange={this.handleChangeIntegration}
    onPageChange={this.handleChange}
  />

  ConsentText = () => <ConsentText
    badge={<this.PlacementModified />}
    type={this.state.type}
    integrationData={this.state.integrationData}
    integration={this.state.integration}
    disabled={!this.state.integrationData.sanitary || this.state.isVersionView}
    onChange={this.handleChangeIntegration}
  />

  Rules = () => <Col>
    <PageRulesTab
      disabled={this.state.isVersionView}
      pageId={this.state.id}
      data={this.state.rules}
      handleChange={this.handleChange}
      onConflict={this.handleConflict}
    />
  </Col>

  render() {
    const { conflictEvent } = this.state
    return (
      <>
        <Formsy onValidSubmit={this.handleValidSubmit}>
          <FormBody>
            <Row className={this.props.view === 'editor' ? undefined : 'hidden'}>
              <Col size={this.hasLivePreview ? '9' : '12'}>
                <this.Sections />
              </Col>
              <this.LivePreview />
            </Row>
            {this.props.view === 'preview' && <FormCard title=''>
              <PreviewTab
                data={this.state}
                extraFields={this.state.extraFields}
                metaFields={this.state.metaFields}
              />
            </FormCard>}
            {this.state.compilerError && <SimpleAlert variant="danger">The page has a compiler error. Please fix it before saving.</SimpleAlert>}
            <FormFooter className='items-center'>
              <SaveVersionButton conflictEvent={conflictEvent} checker={this.checkVersionChanges} onChange={versionChanges => this.setState({ versionChanges })} onCloseView={this.props.onCloseView} hasError={this.state.compilerError} />
            </FormFooter>
          </FormBody>
        </Formsy>
        <this.CustomFields />
      </>
    );
  }

  handleSetTab = tab => {
    if (this.state.activeTab !== tab) {
      this.setState({ activeTab: tab });
    }
  };

  renderTabs() {
    const { type } = this.state;
    const showStandard = type && type !== 'iframe' && type !== 'html';
    const isTcpaCustom = type === 'tcpa-question-custom';
    const activeTab = this.state.activeTab || (showStandard ? 'standard' : 'advanced');

    const tabs = <Tabs value={activeTab}>
      <TabsList>
        {showStandard && <TabsTrigger className='pt-0' value='standard' onClick={() => this.setState({ activeTab: 'standard' })}>Standard</TabsTrigger>}
        <TabsTrigger className='pt-0' value='advanced' onClick={() => this.setState({ activeTab: 'advanced' })}>Advanced</TabsTrigger>
        {this.props.data && <TabsTrigger className='pt-0' value='paths' onClick={() => this.setState({ activeTab: 'paths' })}>Paths</TabsTrigger>}
        {['partner-twyne', 'partner-ifficient', 'partner-twyne-reward'].includes(type) ? <TabsTrigger className='pt-0' value='pixels' onClick={() => this.setState({ activeTab: 'pixels' })}>Pixels</TabsTrigger> : null}
        {isTcpaCustom && <TabsTrigger className='pt-0' value='templateBuilder' onClick={() => this.setState({ activeTab: 'templateBuilder' })}>Template Builder</TabsTrigger>}
      </TabsList>
      <TabsContent
        value='standard'
        forceMount={isTcpaCustom || this.hasCustomField}
        className={isTcpaCustom || this.hasCustomField ? 'data-[state=inactive]:hidden' : undefined}
      >
        <StandardTab
          disabled={this.state.isVersionView}
          type={type}
          sanitary={this.state.integrationData.sanitary}
          handleChangeForm={this.handleChangeIntegration}
          handleClickRestorePlacement={this.handleClickRestorePlacement}
          handleImportPlacement={this.handleImportPlacement}
          data={this.state.integrationData[templateAlias[type]]}
          integrationData={this.state.integrationData}
          integration={this.state.integration}
          handleCustomField={this.handleChange}
          onChangeContent={this.handleChangeTcpaCustomContent}
          onCompilerError={this.handleCompilerError}
          settings={this.hasCustomField && <this.PageSettings />}
        />
      </TabsContent>
      {isTcpaCustom && <TabsContent value='templateBuilder'>
        <TemplateBuilderContent />
      </TabsContent>}
      <TabsContent value='advanced'>
        <AdvancedTab
          disabled={this.state.isVersionView}
          value={this.state.content}
          show={this.state.activeTab === 'advanced'}
          handleChangeContent={this.handleChangeContent}
        />
      </TabsContent>
      <TabsContent value='paths'>
        <PagePathsTab
          pageId={this.state.id}
        />
      </TabsContent>
      <TabsContent value='pixels'>
        <PixelTab
          value={this.state.pixels}
          handleChange={this.handleChange}
          disabled={this.state.isVersionView}
        />
      </TabsContent>
    </Tabs>

    return <TabsWrapper
      isTcpaCustom={isTcpaCustom}
      integrationData={this.state.integrationData}
      extraFields={this.state.extraFields}
      disabled={this.state.isVersionView}
      onChange={this.handleChangeIntegration}
    >
      {tabs}
    </TabsWrapper>
  }
}

const MULTI_BUYER_TYPES = ['tcpa-question', 'tcpa-question-custom', 'tcpa-form']

const SECTION_ORDER = ['general', 'details', 'questionAnswers', 'consentText', 'multiBuyers', 'rules']
const TCPA_QUESTION_SECTION_ORDER = ['general', 'questionAnswers', 'consentText', 'multiBuyers', 'details', 'rules']

const SECTION_HEADER_HEIGHT = 56
const INTERACTIVE = 'button,input,textarea,select,a,label,[role="switch"],[role="combobox"],[contenteditable]'

function SortableSection({ id, children }) {
  const { setNodeRef, style, attributes, listeners } = useSortableDndListItem(id)

  const handleMouseDown = event => {
    const { top } = event.currentTarget.getBoundingClientRect()
    if (event.clientY - top > SECTION_HEADER_HEIGHT || event.target.closest(INTERACTIVE)) {
      return
    }
    listeners.onMouseDown(event)
  }

  return <div ref={setNodeRef} style={removeScaleY(style)} className='col-span-12 relative group' {...attributes} onMouseDown={handleMouseDown}>
    <span className='absolute -left-4 top-4 opacity-0 transition-opacity group-hover:opacity-100 twy-text-neutral cursor-grab'>
      <GripVertical size={16} />
    </span>
    {children}
  </div>
}

const TabsWrapper = ({ children, isTcpaCustom, ...props }) => {
  if (isTcpaCustom) {
    return <TcpaContextProvider {...props}>
      {children}
    </TcpaContextProvider>
  }
  return children
}

PageForm.propTypes = {
  handleSubmit: PropTypes.func,
  onCloseView: PropTypes.oneOfType([PropTypes.func, PropTypes.bool]),
  restored: PropTypes.bool,
  data: PropTypes.any,
  viewClosed: PropTypes.bool,
};

function SaveVersionButton({ checker, onChange, onCloseView, conflictEvent, hasError = false, children }) {
  const [versionChanges, setVersionChanges] = useState(false)
  const disableForm = conflictEvent === 'conflict' || conflictEvent === 'checking'
  useEffect(() => {
    const hasChange = checker()
    if (hasChange !== versionChanges) {
      onChange(hasChange)
      setVersionChanges(hasChange)
    }
  })

  const Save = ({ submit = false, children = buttonLabels[conflictEvent] || 'Save' }) => <>
    <DefaultTooltip triggerProps={{ asChild: true }} content={disableForm ? 'You can\'t save the page with rules conflict!' : hasError ? 'You can\'t save the page with a compiler error!' : ''}>
      <Button
        type={submit ? 'submit' : 'button'}
        color='accent'
        disabled={disableForm || hasError}
      >
        {children}
      </Button>
    </DefaultTooltip>
  </>

  if (onCloseView) {
    return <>
      <div className="flex gap-2">
        <Button type='button' color="accent" onClick={onCloseView(true)}>Restore version</Button>
        <Button variant='ghost' color='danger' type='button' onClick={onCloseView(false)}>Close view</Button>
        {children}
        <Button onClick={() => history.goBack()} variant='ghost'>Cancel</Button>
        <SimpleAlert className='m-0 py-0 px-4 flex items-center' variant="warning">You are seeing a view of one version of this page. To save the editing choose restore or close view.</SimpleAlert>
      </div>
    </>
  }

  if (!versionChanges) {
    return <>
      <Save submit />
      {children}
      <Button onClick={() => history.goBack()} variant='ghost'>Cancel</Button>
    </>
  }

  return <>
    <ConfirmationPopover
      target={<Save />}
      title='Create new page version'
      contentProps={{ container: 'form' }}
    >
      We detected changes that affect the version of the page. Please type a description of these changes.
      <Input className='mt-2 mb-4' required name='versionName' />
      <Save submit>Create</Save>
    </ConfirmationPopover>
    {children}
    <Button onClick={() => history.goBack()} variant='ghost'>Cancel</Button>
  </>
}

export default PageForm;
